import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Play, Square, Clock, Navigation, Calendar, AlertTriangle, Ship, Radio, Share2, Link, Copy, X, Crown, Lock, MapPin, Save, Route, Users, Globe, Bookmark } from 'lucide-react';
import { format, formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { toast } from 'sonner';
import { BackgroundLocationService } from '@/lib/backgroundLocation';
import { TrackingActivePill } from '@/components/TrackingActiveBanner';
import { locationAPI, routesAPI } from '@/lib/api';
import api from '@/lib/api';

const TripScreen = () => {
  const {
    activeTrip,
    tripHistory,
    startTrip,
    endTrip,
    fetchTripHistory,
    myLocation,
    sendAlert,
    isPaid,
    friends,
  } = useApp();

  const [isLoading, setIsLoading] = useState(false);
  const [destination, setDestination] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [tripDuration, setTripDuration] = useState('00:00:00');
  const [isBackgroundTracking, setIsBackgroundTracking] = useState(false);
  const [queueSize, setQueueSize] = useState(0);
  
  // Trip sharing state
  const [shareActive, setShareActive] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [includeSpeed, setIncludeSpeed] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);
  
  // Saved routes state
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [sharedRoutes, setSharedRoutes] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveRouteName, setSaveRouteName] = useState('');
  const [saveRouteDesc, setSaveRouteDesc] = useState('');
  const [saveRoutePublic, setSaveRoutePublic] = useState(false);
  const [savingTripId, setSavingTripId] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingRouteId, setSharingRouteId] = useState(null);
  const [activeTab, setActiveTab] = useState('trips'); // 'trips' or 'routes'

  useEffect(() => {
    fetchTripHistory();
    fetchSavedRoutes();
  }, [fetchTripHistory]);
  
  // Fetch saved routes
  const fetchSavedRoutes = async () => {
    try {
      const [myRoutes, shared] = await Promise.all([
        routesAPI.getMine(),
        routesAPI.getSharedWithMe()
      ]);
      setSavedRoutes(myRoutes.data || []);
      setSharedRoutes(shared.data || []);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  };
  
  // Save route from trip
  const handleSaveRoute = async () => {
    if (!saveRouteName.trim()) {
      toast.error('Please enter a route name');
      return;
    }
    
    try {
      await routesAPI.save({
        trip_id: savingTripId,
        name: saveRouteName,
        description: saveRouteDesc || null,
        is_public: saveRoutePublic
      });
      toast.success('Route saved!');
      setShowSaveModal(false);
      setSaveRouteName('');
      setSaveRouteDesc('');
      setSaveRoutePublic(false);
      setSavingTripId(null);
      fetchSavedRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save route');
    }
  };
  
  // Share route with friend
  const handleShareRoute = async (friendId) => {
    try {
      await routesAPI.share(sharingRouteId, friendId);
      toast.success('Route shared!');
      setShowShareModal(false);
      setSharingRouteId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share route');
    }
  };
  
  // Delete saved route
  const handleDeleteRoute = async (routeId) => {
    try {
      await routesAPI.delete(routeId);
      toast.success('Route deleted');
      fetchSavedRoutes();
    } catch (error) {
      toast.error('Failed to delete route');
    }
  };
  
  // Check share status when trip is active
  useEffect(() => {
    const checkShareStatus = async () => {
      if (activeTrip) {
        try {
          const response = await api.get('/trips/share/status');
          const shares = response.data.active_shares || [];
          if (shares.length > 0) {
            const share = shares[0];
            setShareActive(true);
            setShareUrl(`${window.location.origin}/share/${share.token}`);
            setIncludeSpeed(share.include_speed || false);
          } else {
            setShareActive(false);
            setShareUrl('');
          }
        } catch (error) {
          console.error('Failed to check share status:', error);
        }
      } else {
        setShareActive(false);
        setShareUrl('');
      }
    };
    
    checkShareStatus();
  }, [activeTrip]);

  // Check if background tracking is enabled in settings
  const isBackgroundTrackingEnabled = () => {
    return localStorage.getItem('boat360_bg_tracking') === 'true';
  };

  // Update trip duration timer
  useEffect(() => {
    if (!activeTrip) {
      setTripDuration('00:00:00');
      return;
    }

    const updateDuration = () => {
      const startTime = new Date(activeTrip.started_at);
      const now = new Date();
      const totalSeconds = differenceInSeconds(now, startTime);
      
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      setTripDuration(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [activeTrip]);

  // Handle background location updates
  const handleLocationUpdate = useCallback(async (locationData) => {
    try {
      await locationAPI.update(locationData);
    } catch (error) {
      console.error('Failed to send background location:', error);
    }
  }, []);

  // Start/stop background tracking when trip status changes
  useEffect(() => {
    const manageBackgroundTracking = async () => {
      if (activeTrip && isBackgroundTrackingEnabled()) {
        // Start background tracking
        const success = await BackgroundLocationService.startTracking({
          onLocation: handleLocationUpdate,
          onError: (error) => {
            console.error('Background location error:', error);
            toast.error('Background tracking error');
          },
        });
        
        if (success) {
          setIsBackgroundTracking(true);
          toast.success('Trip tracking started');
        }
      } else if (!activeTrip && isBackgroundTracking) {
        // Stop background tracking
        await BackgroundLocationService.stopTracking();
        setIsBackgroundTracking(false);
      }
    };

    manageBackgroundTracking();
  }, [activeTrip, handleLocationUpdate, isBackgroundTracking]);

  // Update queue size display
  useEffect(() => {
    if (isBackgroundTracking) {
      const interval = setInterval(() => {
        setQueueSize(BackgroundLocationService.getQueueSize());
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isBackgroundTracking]);

  const handleStartTrip = async () => {
    setIsLoading(true);
    try {
      const tripData = {
        destination: destination.trim() || null,
        departure_at: departureTime ? new Date(departureTime).toISOString() : null,
        expected_return_at: expectedReturn ? new Date(expectedReturn).toISOString() : null,
      };
      await startTrip(tripData);
      // Clear form
      setDestination('');
      setDepartureTime('');
      setExpectedReturn('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndTrip = async () => {
    setIsLoading(true);
    try {
      // Stop background tracking first
      if (isBackgroundTracking) {
        await BackgroundLocationService.stopTracking();
        setIsBackgroundTracking(false);
      }
      
      await endTrip();
      fetchTripHistory();
      toast.success('Trip ended');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end trip');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSOS = async () => {
    if (window.confirm('Send SOS alert to all your friends?')) {
      try {
        await sendAlert('SOS', 'Emergency! Need assistance!');
      } catch (error) {
        toast.error('Failed to send SOS');
      }
    }
  };

  const handleImOk = async () => {
    try {
      await sendAlert('IM_OK', 'All good here!');
    } catch (error) {
      toast.error('Failed to send status');
    }
  };
  
  // Trip sharing handlers
  const handleStartSharing = async () => {
    setSharingLoading(true);
    try {
      const response = await api.post('/trips/share/start', { include_speed: includeSpeed });
      const data = response.data;
      
      if (data.success) {
        const fullUrl = `${window.location.origin}${data.share_url}`;
        setShareUrl(fullUrl);
        setShareActive(true);
        toast.success('Trip sharing started');
      }
    } catch (error) {
      toast.error('Failed to start sharing');
    } finally {
      setSharingLoading(false);
    }
  };
  
  const handleStopSharing = async () => {
    setSharingLoading(true);
    try {
      await api.post('/trips/share/stop');
      setShareActive(false);
      setShareUrl('');
      toast.success('Trip sharing stopped');
    } catch (error) {
      toast.error('Failed to stop sharing');
    } finally {
      setSharingLoading(false);
    }
  };
  
  const handleCopyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="trip-screen">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4">
        <h1 className="text-xl font-bold text-gray-800">Trip</h1>
        <p className="text-sm text-gray-500">
          {activeTrip ? 'Currently on a trip' : 'Start a trip to share your status'}
        </p>
      </div>

      <ScrollArea className="flex-1 px-4 py-4">
        {/* Active Trip or Start Trip */}
        {activeTrip ? (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  ON TRIP
                </CardTitle>
                <div className="flex items-center gap-2">
                  {isBackgroundTracking && (
                    <TrackingActivePill isVisible={true} source="bg" />
                  )}
                  <Badge className="bg-green-500">{tripDuration}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Destination */}
              {activeTrip.destination && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-cyan-600" />
                    <div>
                      <p className="text-xs text-cyan-600 uppercase">Destination</p>
                      <p className="font-semibold text-cyan-800">{activeTrip.destination}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Background tracking indicator */}
              {isBackgroundTracking && (
                <div className="bg-green-100 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                  <Radio className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">
                    Background tracking active
                    {queueSize > 0 && ` • ${queueSize} updates queued`}
                  </span>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Started</p>
                  <p className="font-medium text-gray-800">
                    {format(new Date(activeTrip.started_at), 'h:mm a')}
                  </p>
                </div>
                {activeTrip.departure_at && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Departure</p>
                    <p className="font-medium text-gray-800">
                      {format(new Date(activeTrip.departure_at), 'h:mm a')}
                    </p>
                  </div>
                )}
                {activeTrip.expected_return_at && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Expected Return</p>
                    <p className="font-medium text-gray-800">
                      {format(new Date(activeTrip.expected_return_at), 'h:mm a')}
                    </p>
                  </div>
                )}
              </div>

              {myLocation && (
                <div className="bg-white rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                      <Navigation className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Current Speed</p>
                      <p className="text-2xl font-bold text-gray-800">
                        {myLocation.speed.toFixed(1)} <span className="text-sm font-normal">kt</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Trip Sharing Section */}
              <div className="bg-white rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-cyan-600" />
                    <span className="font-medium text-gray-700">Share Trip</span>
                    {!isPaid && (
                      <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-xs">
                        <Crown className="w-3 h-3 mr-1" />
                        Plus
                      </Badge>
                    )}
                  </div>
                  {shareActive && (
                    <Badge className="bg-cyan-500">Live</Badge>
                  )}
                </div>
                
                {!isPaid ? (
                  <div className="text-center py-3">
                    <Lock className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-2">
                      Trip sharing requires Boat 360 Plus
                    </p>
                    <p className="text-xs text-gray-400">
                      Share your live trip with anyone using a link
                    </p>
                  </div>
                ) : shareActive ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                      <Link className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-xs text-gray-600 truncate flex-1">
                        {shareUrl}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCopyShareUrl}
                        className="p-1 h-auto"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button
                      onClick={handleStopSharing}
                      disabled={sharingLoading}
                      variant="outline"
                      size="sm"
                      className="w-full text-red-600 border-red-300"
                      data-testid="stop-sharing-btn"
                    >
                      <X className="w-3 h-3 mr-1" />
                      {sharingLoading ? 'Stopping...' : 'Stop Sharing'}
                    </Button>
                    <p className="text-xs text-gray-400 text-center">
                      Anyone with this link can see your live location
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="include-speed" className="text-sm text-gray-600">
                        Include speed & heading
                      </Label>
                      <Switch
                        id="include-speed"
                        checked={includeSpeed}
                        onCheckedChange={setIncludeSpeed}
                      />
                    </div>
                    <Button
                      onClick={handleStartSharing}
                      disabled={sharingLoading}
                      className="w-full bg-cyan-500 hover:bg-cyan-600"
                      data-testid="start-sharing-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      {sharingLoading ? 'Creating Link...' : 'Create Share Link'}
                    </Button>
                    <p className="text-xs text-gray-400 text-center">
                      Share your trip with anyone (expires when trip ends)
                    </p>
                  </div>
                )}
              </div>

              <Button
                onClick={handleEndTrip}
                disabled={isLoading}
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50"
                data-testid="end-trip-btn"
              >
                <Square className="w-4 h-4 mr-2" />
                {isLoading ? 'Ending...' : 'End Trip'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Ship className="w-5 h-5 text-cyan-500" />
                Start a Trip
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Destination */}
              <div>
                <Label htmlFor="destination" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  Where are you going?
                </Label>
                <Input
                  id="destination"
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="e.g., Key West, Fishing spot, Marina Bay"
                  className="mt-2"
                  data-testid="destination-input"
                />
              </div>
              
              {/* Departure Time */}
              <div>
                <Label htmlFor="departure-time" className="flex items-center gap-2">
                  <Play className="w-4 h-4 text-gray-500" />
                  Departure Time
                </Label>
                <Input
                  id="departure-time"
                  type="datetime-local"
                  value={departureTime}
                  onChange={(e) => setDepartureTime(e.target.value)}
                  className="mt-2"
                  data-testid="departure-time-input"
                />
                <p className="text-xs text-gray-500 mt-1">When are you leaving?</p>
              </div>
              
              {/* Expected Return */}
              <div>
                <Label htmlFor="expected-return" className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  Expected Return
                </Label>
                <Input
                  id="expected-return"
                  type="datetime-local"
                  value={expectedReturn}
                  onChange={(e) => setExpectedReturn(e.target.value)}
                  className="mt-2"
                  data-testid="expected-return-input"
                />
                <p className="text-xs text-gray-500 mt-1">When do you plan to be back?</p>
              </div>
              
              <Button
                onClick={handleStartTrip}
                disabled={isLoading}
                className="w-full bg-green-500 hover:bg-green-600"
                data-testid="start-trip-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {isLoading ? 'Starting...' : 'Start Trip'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Alerts */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Quick Alerts</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-3">
            <Button
              onClick={handleSOS}
              variant="destructive"
              className="flex-1"
              data-testid="sos-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              SOS
            </Button>
            <Button
              onClick={handleImOk}
              variant="outline"
              className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
              data-testid="im-ok-btn"
            >
              I&apos;m OK
            </Button>
          </CardContent>
        </Card>

        {/* Tabs: Trips / Saved Routes */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'trips' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('trips')}
            className={activeTab === 'trips' ? 'bg-cyan-500' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Trips
          </Button>
          <Button
            variant={activeTab === 'routes' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('routes')}
            className={activeTab === 'routes' ? 'bg-cyan-500' : ''}
          >
            <Route className="w-4 h-4 mr-2" />
            Saved Routes
            {(savedRoutes.length + sharedRoutes.length) > 0 && (
              <Badge className="ml-2 bg-white/20">{savedRoutes.length + sharedRoutes.length}</Badge>
            )}
          </Button>
        </div>

        {/* Trip History Tab */}
        {activeTab === 'trips' && (
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Recent Trips
            </h2>
            {tripHistory.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-6 text-center">
                  <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No trips yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {tripHistory.map((trip) => {
                  const startTime = new Date(trip.started_at);
                  const endTime = trip.ended_at ? new Date(trip.ended_at) : null;
                  const duration = endTime
                    ? Math.floor(differenceInSeconds(endTime, startTime) / 60)
                    : null;
                  const isEnded = trip.status === 'ENDED';

                  return (
                    <Card key={trip.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                              <Calendar className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">
                                {trip.destination || format(startTime, 'MMM d, yyyy')}
                              </p>
                              <p className="text-sm text-gray-500">
                                {format(startTime, 'h:mm a')}
                                {endTime && ` - ${format(endTime, 'h:mm a')}`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            {duration !== null ? (
                              <Badge variant="secondary">
                                {duration >= 60
                                  ? `${Math.floor(duration / 60)}h ${duration % 60}m`
                                  : `${duration}m`}
                              </Badge>
                            ) : (
                              <Badge className="bg-green-500">Active</Badge>
                            )}
                          </div>
                        </div>
                        {/* Trip stats and save button */}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t">
                          <div className="flex gap-4 text-sm">
                            {trip.distance_nm > 0 && (
                              <div className="flex items-center gap-1 text-gray-600">
                                <Navigation className="w-4 h-4" />
                                <span>{trip.distance_nm.toFixed(1)} nm</span>
                              </div>
                            )}
                            {trip.max_speed > 0 && (
                              <div className="flex items-center gap-1 text-gray-600">
                                <Play className="w-4 h-4" />
                                <span>Max {trip.max_speed.toFixed(1)} kt</span>
                              </div>
                            )}
                          </div>
                          {isEnded && trip.distance_nm > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-cyan-600 hover:bg-cyan-50"
                              onClick={() => {
                                setSavingTripId(trip.id);
                                setSaveRouteName(trip.destination || `Route ${format(startTime, 'MMM d')}`);
                                setShowSaveModal(true);
                              }}
                            >
                              <Save className="w-4 h-4 mr-1" />
                              Save Route
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Saved Routes Tab */}
        {activeTab === 'routes' && (
          <div className="space-y-4">
            {/* My Routes */}
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Bookmark className="w-4 h-4" />
                My Saved Routes
              </h2>
              {savedRoutes.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-6 text-center">
                    <Route className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No saved routes yet</p>
                    <p className="text-xs text-gray-400 mt-1">Save a route from your trip history</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {savedRoutes.map((route) => (
                    <Card key={route.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                              <Route className="w-5 h-5 text-cyan-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{route.name}</p>
                              {route.description && (
                                <p className="text-sm text-gray-500">{route.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {route.is_public && (
                              <Badge variant="secondary" className="text-xs">
                                <Globe className="w-3 h-3 mr-1" />
                                Public
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t">
                          <div className="flex gap-3 text-sm text-gray-600">
                            {route.distance_nm && (
                              <span className="flex items-center gap-1">
                                <Navigation className="w-4 h-4" />
                                {route.distance_nm.toFixed(1)} nm
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {route.points?.length || 0} pts
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-cyan-600"
                              onClick={() => {
                                setSharingRouteId(route.id);
                                setShowShareModal(true);
                              }}
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => handleDeleteRoute(route.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Shared With Me */}
            {sharedRoutes.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Shared With Me
                </h2>
                <div className="space-y-3">
                  {sharedRoutes.map((route) => (
                    <Card key={route.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <Route className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{route.name}</p>
                              <p className="text-sm text-gray-500">From {route.owner_name}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t text-sm text-gray-600">
                          {route.distance_nm && (
                            <span className="flex items-center gap-1">
                              <Navigation className="w-4 h-4" />
                              {route.distance_nm.toFixed(1)} nm
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {route.points?.length || 0} pts
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Save Route Modal */}
        <Dialog open={showSaveModal} onOpenChange={setShowSaveModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save Route</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="route-name">Route Name</Label>
                <Input
                  id="route-name"
                  value={saveRouteName}
                  onChange={(e) => setSaveRouteName(e.target.value)}
                  placeholder="e.g., Miami to Key West"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="route-desc">Description (optional)</Label>
                <Input
                  id="route-desc"
                  value={saveRouteDesc}
                  onChange={(e) => setSaveRouteDesc(e.target.value)}
                  placeholder="Great route for fishing spots..."
                  className="mt-2"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={saveRoutePublic}
                  onCheckedChange={setSaveRoutePublic}
                />
                <div>
                  <Label>Make Public</Label>
                  <p className="text-xs text-gray-500">Other boaters can discover this route</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveModal(false)}>Cancel</Button>
              <Button onClick={handleSaveRoute} className="bg-cyan-500">
                <Save className="w-4 h-4 mr-2" />
                Save Route
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share Route Modal */}
        <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Share Route with Friend</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {friends && friends.length > 0 ? (
                <div className="space-y-2">
                  {friends.map((friend) => (
                    <Button
                      key={friend.friend_user_id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => handleShareRoute(friend.friend_user_id)}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      {friend.display_name || friend.username}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">No friends to share with yet</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </ScrollArea>
    </div>
  );
};

export default TripScreen;
